import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import DataTable_Component from "../../components/leaves/Data Table Component";

import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layouts from "../../layouts/layouts";

import { faHistory } from "@fortawesome/free-solid-svg-icons";

export default function Leaves() {
  const navigate = useNavigate();
  const title = "User Leaves";
  const [apistate, setApiState] = useState([]);
  const [apicol, setApiCol] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const handleStatusChange = async (leaveId, status) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/v2/approve-leave/${leaveId}`,
        { status },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchData(currentPage); // Refresh data after status change
    } catch (error) {
      console.error("Error updating leave status: ", error);
    }
  };

  const columns = [
    {
      name: <b>User Name</b>,
      selector: (row) => row.userName,
      sortable: true,
    },
    {
      name: <b>Phone Number</b>,
      selector: (row) => row.userPhone || "N/A",
      sortable: true,
    },
    {
      name: <b>Leave Type</b>,
      selector: (row) => row?.id_leaveType?.name || "N/A",
      sortable: true,
    },
    {
      name: <b>From</b>,
      selector: (row) => moment(row.startDate).format("DD-MM-YYYY"),
      sortable: true,
    },
    {
      name: <b>To</b>,
      selector: (row) => moment(row.endDate).format("DD-MM-YYYY"),
      sortable: true,
    },
    {
      name: <b>Total Days</b>,
      selector: (row) => row.daysRequested || "N/A",
      sortable: true,
    },
    {
      name: <b>Reason</b>,
      selector: (row) => row.reason || "N/A",
      sortable: true,
    },
    {
      name: <b>Pending Balance</b>,
      selector: (row) => row.leaveBalance,
      sortable: true,
    },
    {
      name: <b>Unpaid Leaves Taken</b>,
      selector: (row) => row.unpaidLeavesTaken,
      sortable: true,
    },

    {
      name: <b>Approved By</b>,
      selector: (row) => row?.approvedBy?.name || "N/A",
      sortable: true,
    },

    {
      name: <b>Status</b>,
      cell: (row) => {
        const getStatusStyle = (status) => {
          switch (status) {
            case "Pending":
              return { backgroundColor: "yellow", color: "black" };
            case "Approved":
              return { backgroundColor: "green", color: "white" };
            case "Rejected":
              return { backgroundColor: "red", color: "white" };
            default:
              return {};
          }
        };

        const handleChange = (e) => {
          handleStatusChange(row._id, e.target.value);
        };

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <select
              value={row.status}
              onChange={handleChange}
              disabled={row.status !== "Pending"}
              style={{
                border: "none",
                backgroundColor: getStatusStyle(row.status).backgroundColor,
                color: getStatusStyle(row.status).color,
                padding: "6px",
                borderRadius: "4px",
                width: "80px",
                cursor: row.status === "Pending" ? "pointer" : "not-allowed",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              {row.status === "Pending" && (
                <>
                  <option value="Pending" style={{ backgroundColor: "white" }}>
                    Pending
                  </option>
                  <option value="Approved" style={{ backgroundColor: "white" }}>
                    Approved
                  </option>
                  <option value="Rejected" style={{ backgroundColor: "white" }}>
                    Rejected
                  </option>
                </>
              )}
              {row.status !== "Pending" && (
                <option value={row.status}>{row.status}</option>
              )}
            </select>
          </div>
        );
      },
      sortable: true,
    },

    {
      name: <b>History</b>,
      cell: (row) => (
        <>
          <button
            onClick={() => handleLeaveHistory(row)}
            className="btn btn-warning btn-sm ms-1"
          >
            <FontAwesomeIcon icon={faHistory} />
          </button>
        </>
      ),
    },
  ];

  const fetchLeaveBalance = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v2/leave-balance/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const lb = response.data.leaveBalance;
        return {
          leaveBalance: Math.max(0, lb.totalCasualLeaves - lb.totalLeavesTaken),
          unpaidLeavesTaken: lb.unpaidLeavesTaken || 0,
        };
      } else {
        return {
          leaveBalance: "N/A",
          unpaidLeavesTaken: "N/A",
        };
      }
    } catch (error) {
      console.error(`Error fetching leave balance for user ${userId}:`, error);
      return {
        leaveBalance: "N/A",
        unpaidLeavesTaken: "N/A",
      };
    }
  };
  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v2/leave-requests`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const leaveData = response.data.leaveRequests;

      if (Array.isArray(leaveData)) {
        // Filter: exclude leaves missing ALL of userName, reason, and leaveType
        const validLeaves = leaveData.filter((leave) => {
          const hasUserName = leave?.id_user?.name;
          const hasReason = leave?.reason;
          const hasLeaveType = leave?.id_leaveType?.name;

          // Only skip if all three are missing
          return hasUserName || hasReason || hasLeaveType;
        });

        console.log("Filtered valid leaves:", validLeaves.length);

        const transformedData = await Promise.all(
          validLeaves.map(async (leave) => {
            const userId = leave.id_user?._id;
            const userName = leave.id_user?.name || "N/A";
            const userPhone = leave.id_user?.phonenumber || "N/A";

            let balanceData = {
              leaveBalance: "N/A",
              unpaidLeavesTaken: "N/A",
            };

            if (userId) {
              balanceData = await fetchLeaveBalance(userId);
            }

            return {
              ...leave,
              userName,
              userId,
              userPhone,
              ...balanceData,
            };
          })
        );

        setApiState(transformedData);
      } else {
        console.error("Unexpected API data format", response.data);
        setApiState([]);
      }

      setApiCol(columns);
    } catch (error) {
      console.error("Error fetching leave data:", error);
      setApiState([]);
    }
  };

  // const fetchData = async () => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     if (!token) {
  //       console.error("No token found");
  //       return;
  //     }

  //     const response = await axios.get(
  //       `${process.env.REACT_APP_BASE_URL}/api/v2/leave-requests`,
  //       {
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     const leaveData = response.data.leaveRequests;

  //     if (Array.isArray(leaveData)) {
  //       const transformedData = await Promise.all(
  //         leaveData.map(async (leave) => {
  //           const userId = leave.id_user?._id;
  //           const userName = leave.id_user?.name || "N/A";
  //           const userPhone = leave.id_user?.phonenumber || "N/A";

  //           let balanceData = {
  //             leaveBalance: "N/A",
  //             unpaidLeavesTaken: "N/A",
  //           };

  //           if (userId) {
  //             balanceData = await fetchLeaveBalance(userId);
  //           }

  //           return {
  //             ...leave,
  //             userName,
  //             userId,
  //             userPhone,
  //             ...balanceData,
  //           };
  //         })
  //       );

  //       setApiState(transformedData);
  //     } else {
  //       console.error("Unexpected API data format", response.data);
  //       setApiState([]);
  //     }

  //     setApiCol(columns);
  //   } catch (error) {
  //     console.error("Error fetching leave data:", error);
  //     setApiState([]);
  //   }
  // };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handleLeaveHistory = (row) => {
    navigate(`/leavehistory/${row.userId}`);
  };

  return (
    <>
      <Layouts />

      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              <div className="card-header d-flex justify-content-between align-items-center border-bottom pb-1">
                <h4>{title}</h4>
              </div>
              <div className="card-body">
                {apistate && apistate.length > 0 ? (
                  <DataTable_Component
                    searchBy="name"
                    title_btn={true}
                    title="Leave List"
                    apidata={apistate}
                    columns={apicol}
                  />
                ) : (
                  <p>Loading...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
